import { toast } from 'react-toastify';
import React, { useState, useRef, useEffect } from 'react';
import { Info, Camera, GripVertical } from 'lucide-react';
import { uploadImage, getImages, deleteImage, updateImageDescription, bulkUpdateImageOrder } from '../services/uploadService';
import ConfirmDialog from './ConfirmDialog';
import RichTextEditor from './RichTextEditor';
import '../styles/PhotoGallery.css';

interface PhotoGalleryProps {
  workspaceId: string;
  entityType: string;
  entityId: string;
  category?: string;
  imageType?: string;
  maxImages?: number;
  allowMultiple?: boolean;
}

interface GalleryImage {
  _id: string;
  url: string;
  key: string;
  imageType: string;
  size: number;
  mimeType: string;
  description?: string;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  workspaceId,
  entityType,
  entityId,
  category,
  imageType,
  maxImages,
  allowMultiple = true
}) => {
  // Use category as imageType if provided
  const effectiveImageType = category || imageType || 'photo';
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [descriptionData, setDescriptionData] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchImages();
  }, [workspaceId, entityType, entityId, effectiveImageType]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const fetchedImages = await getImages(workspaceId, {
        entityType,
        entityId,
        imageType: effectiveImageType
      });
      console.log('Fetched images:', fetchedImages);
      setImages(fetchedImages);
    } catch (err: any) {
      console.error('Error fetching images:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const allFiles = Array.from(e.dataTransfer.files);
      await uploadImages(allFiles);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      await uploadImages(files);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const uploadImages = async (files: File[]) => {
    // Separate valid image files from invalid files
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    const invalidFiles = files.filter(file => !file.type.startsWith('image/'));

    // Show error for invalid files with their names
    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map(f => f.name).join(', ');
      toast.error(`File not compatible: ${invalidFileNames}. Only image files are allowed.`);
    }

    // If no valid files, return early
    if (validFiles.length === 0) {
      return;
    }

    if (maxImages && images.length + validFiles.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    if (!allowMultiple && validFiles.length > 1) {
      toast.info('Only one image allowed');
      return;
    }

    try {
      setUploading(true);

      // Upload all valid files in parallel
      const uploadPromises = validFiles.map(file =>
        uploadImage({
          workspaceId,
          file,
          entityType,
          entityId,
          imageType: effectiveImageType
        })
      );

      const uploadedImages = await Promise.all(uploadPromises);
      setImages(prev => [...prev, ...uploadedImages]);
      toast.success(`${uploadedImages.length} image${uploadedImages.length > 1 ? 's' : ''} uploaded successfully`);
    } catch (err: any) {
      console.error('Error uploading images:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to upload images';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleAddClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent, imageId: string) => {
    e.stopPropagation();
    setImageToDelete(imageId);
    setConfirmDialogOpen(true);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handlePrevImage();
    if (e.key === 'ArrowRight') handleNextImage();
    if (e.key === 'Escape') setLightboxOpen(false);
  };

  const handleEditDescription = (e: React.MouseEvent, imageId: string, currentDescription?: string) => {
    e.stopPropagation();
    setEditingImageId(imageId);
    setDescriptionData(currentDescription || '');
    setDescriptionDialogOpen(true);
  };

  const handleSaveDescription = async () => {
    if (!editingImageId) return;

    try {
      setSavingDescription(true);
      await updateImageDescription(workspaceId, editingImageId, descriptionData);

      // Update local state
      setImages(prev => prev.map(img =>
        img._id === editingImageId ? { ...img, description: descriptionData } : img
      ));

      setDescriptionDialogOpen(false);
      setEditingImageId(null);
      setDescriptionData('');
      toast.success('Description updated successfully');
    } catch (err: any) {
      console.error('Error updating description:', err);
      toast.error(err.response?.data?.message || 'Failed to update description');
    } finally {
      setSavingDescription(false);
    }
  };

  const handleCancelDescription = () => {
    setDescriptionDialogOpen(false);
    setEditingImageId(null);
    setDescriptionData('');
  };

  const handleImageDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', ''); // Required for Firefox
  };

  const handleImageDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleImageDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleImageDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder images array
    const newImages = [...images];
    const [draggedImage] = newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    // Update local state immediately for better UX
    setImages(newImages);
    setDraggedIndex(null);
    setDragOverIndex(null);

    // Update order in backend using bulk update (single API call)
    try {
      const updates = newImages.map((image, index) => ({
        imageId: image._id,
        order: index
      }));

      await bulkUpdateImageOrder(workspaceId, updates);
      toast.success('Image order updated');
    } catch (err: any) {
      console.error('Error updating image order:', err);
      toast.error('Failed to update image order');
      // Revert on error
      fetchImages();
    }
  };

  const handleImageDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDeleteConfirm = async () => {
    if (!imageToDelete) return;

    try {
      setDeleting(true);
      await deleteImage(workspaceId, imageToDelete);
      setImages(prev => prev.filter(img => img._id !== imageToDelete));
      setConfirmDialogOpen(false);
      setImageToDelete(null);
      toast.success('Image deleted successfully');
    } catch (err: any) {
      console.error('Error deleting image:', err);
      toast.error(err.response?.data?.message || 'Failed to delete image');
    } finally {
      setDeleting(false);
    }
  };

  const canUploadMore = !maxImages || images.length < maxImages;

  return (
    <div className="photo-gallery">
      {loading ? (
        <div className="photo-gallery-loading">Loading images...</div>
      ) : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple={allowMultiple}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {/* Entire container is drag and drop when there are images */}
          <div
            className={`photo-gallery-container ${dragActive ? 'drag-active' : ''} ${images.length > 0 ? 'has-images' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {/* Empty State with Upload Area */}
            {images.length === 0 && canUploadMore && (
              <div
                className="photo-gallery-upload"
                onClick={handleAddClick}
              >
                <div className="photo-gallery-upload-content">
                  {uploading ? (
                    <>
                      <div className="upload-spinner"></div>
                      <p>Uploading...</p>
                    </>
                  ) : (
                    <>
                      <div className="upload-icon">
                        <Camera size={48} color="#9CA3AF" />
                      </div>
                      <p className="upload-text">Drag & drop images here</p>
                      <p className="upload-subtext">or click to browse</p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Image Grid with Add Button */}
            {images.length > 0 && (
              <div className="photo-gallery-grid">
                {images.map((image, index) => (
                  <div
                    key={image._id}
                    className={`photo-gallery-item ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                    draggable
                    onDragStart={(e) => handleImageDragStart(e, index)}
                    onDragOver={(e) => handleImageDragOver(e, index)}
                    onDragLeave={handleImageDragLeave}
                    onDrop={(e) => handleImageDrop(e, index)}
                    onDragEnd={handleImageDragEnd}
                    onClick={() => handleImageClick(index)}
                  >
                    <div className="photo-gallery-drag-handle">
                      <GripVertical size={16} color="#6B7280" />
                    </div>
                    <img src={image.url} alt="" className="photo-gallery-image" />
                    {image.description && (
                      <div
                        className="photo-gallery-description-overlay"
                        dangerouslySetInnerHTML={{ __html: image.description }}
                      />
                    )}
                    <div className="photo-gallery-actions">
                      <button
                        type="button"
                        className="photo-gallery-info"
                        onClick={(e) => handleEditDescription(e, image._id, image.description)}
                        title="Edit description"
                      >
                        <Info size={16} />
                      </button>
                      <button
                        type="button"
                        className="photo-gallery-delete"
                        onClick={(e) => handleDeleteClick(e, image._id)}
                        title="Delete image"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}

                {/* Add More Button */}
                {canUploadMore && (
                  <div className="photo-gallery-add" onClick={handleAddClick}>
                    <div className="photo-gallery-add-content">
                      {uploading ? (
                        <div className="upload-spinner"></div>
                      ) : (
                        <>
                          <div className="add-icon">+</div>
                          <p className="add-text">Add Photo</p>
                          <p className="add-subtext">Drag and Drop Photos</p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty State when max reached */}
            {images.length === 0 && !canUploadMore && (
              <div className="photo-gallery-empty">No images uploaded yet</div>
            )}
          </div>
        </>
      )}

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div
          className="photo-lightbox-overlay"
          onClick={() => setLightboxOpen(false)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <button
            type="button"
            className="photo-lightbox-close"
            onClick={() => setLightboxOpen(false)}
          >
            ✕
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                className="photo-lightbox-arrow photo-lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevImage();
                }}
              >
                ‹
              </button>
              <button
                type="button"
                className="photo-lightbox-arrow photo-lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextImage();
                }}
              >
                ›
              </button>
            </>
          )}

          <div className="photo-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[currentImageIndex]?.url}
              alt=""
              className="photo-lightbox-image"
            />
            <div className="photo-lightbox-info">
              <div className="photo-lightbox-counter">
                {currentImageIndex + 1} / {images.length}
              </div>
              {images[currentImageIndex]?.description && (
                <div
                  className="photo-lightbox-description"
                  dangerouslySetInnerHTML={{ __html: images[currentImageIndex].description || '' }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Description Editor Dialog */}
      {descriptionDialogOpen && (
        <div className="photo-description-modal-overlay" onClick={handleCancelDescription}>
          <div className="photo-description-modal" onClick={(e) => e.stopPropagation()}>
            <div className="photo-description-header">
              <h3>Edit Description</h3>
              <button type="button" className="photo-description-close" onClick={handleCancelDescription}>
                ✕
              </button>
            </div>
            <div className="photo-description-content">
              <RichTextEditor
                content={descriptionData}
                onChange={setDescriptionData}
              />
            </div>
            <div className="photo-description-footer">
              <button
                type="button"
                className="photo-description-cancel"
                onClick={handleCancelDescription}
                disabled={savingDescription}
              >
                Cancel
              </button>
              <button
                type="button"
                className="photo-description-save"
                onClick={handleSaveDescription}
                disabled={savingDescription}
              >
                {savingDescription ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        onClose={() => {
          if (!deleting) {
            setConfirmDialogOpen(false);
            setImageToDelete(null);
          }
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Image"
        message="Are you sure you want to delete this image? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonStyle="danger"
        loading={deleting}
      />
    </div>
  );
};

export default PhotoGallery;
